/**
 * Subscription Slice — MangoTV
 * Pattern: mirrors GloTV subscriptionSlice.js
 *
 * Manages:
 *   - Available packages (plans)
 *   - Active subscription status
 *   - Purchase flow (loading, modal, payment)
 *   - Purchase history / billing
 *   - Premium subscription flag (persisted to localStorage)
 */

import { createSlice } from "@reduxjs/toolkit";

const subscriptionSlice = createSlice({
  name: "subscription",
  initialState: {
    // Available packages from CRM
    packages: [],
    packagesLoading: false,
    packagesError: null,

    // Product categories from CRM landing page
    productCategories: [],
    productsLoading: false,
    productsError: null,

    // Active subscription
    activeSubscription: null,
    hasActiveSubscription: false,
    premiumSub:
      JSON.parse(localStorage.getItem("mango_premium_sub")) || false,
    // Granular: which product IDs are actively subscribed
    activeProductIds:
      JSON.parse(localStorage.getItem("mango_active_product_ids")) || [],

    // Purchase flow
    isLoading: false,
    modalOpen: false,
    selectedProduct: null, // { id, name, price, currency }
    paymentInitiated: false,

    // Purchase history / billing
    purchaseHistory: [],
    historyLoading: false,
  },
  reducers: {
    // --- Packages ---
    fetchPackagesBegin: (state) => {
      state.packagesLoading = true;
      state.packagesError = null;
    },
    fetchPackagesSuccess: (state, action) => {
      state.packages = action.payload;
      state.packagesLoading = false;
    },
    fetchPackagesError: (state, action) => {
      state.packagesError = action.payload;
      state.packagesLoading = false;
    },

    // --- Product Categories (from CRM landing) ---
    fetchProductsBegin: (state) => {
      state.productsLoading = true;
      state.productsError = null;
    },
    fetchProductsSuccess: (state, action) => {
      state.productCategories = action.payload;
      state.productsLoading = false;
    },
    fetchProductsError: (state, action) => {
      state.productsError = action.payload;
      state.productsLoading = false;
    },

    // --- Active subscription ---
    setActiveSubscription: (state, action) => {
      state.activeSubscription = action.payload;
      state.hasActiveSubscription = !!action.payload;
    },
    setPremiumSub: (state, action) => {
      state.premiumSub = action.payload;
      localStorage.setItem(
        "mango_premium_sub",
        JSON.stringify(action.payload)
      );
    },
    setActiveProductIds: (state, action) => {
      state.activeProductIds = action.payload;
      localStorage.setItem(
        "mango_active_product_ids",
        JSON.stringify(action.payload)
      );
    },

    // --- Purchase flow ---
    setLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    openSubscriptionModal: (state, action) => {
      state.modalOpen = true;
      state.selectedProduct = action.payload || null;
    },
    closeSubscriptionModal: (state) => {
      state.modalOpen = false;
      state.selectedProduct = null;
    },
    setPaymentInitiated: (state, action) => {
      state.paymentInitiated = action.payload;
    },

    // --- Purchase history ---
    fetchHistoryBegin: (state) => {
      state.historyLoading = true;
    },
    fetchHistorySuccess: (state, action) => {
      state.purchaseHistory = action.payload;
      state.historyLoading = false;
    },

    // --- Reset ---
    resetSubscription: (state) => {
      state.activeSubscription = null;
      state.hasActiveSubscription = false;
      state.premiumSub = false;
      state.activeProductIds = [];
      state.purchaseHistory = [];
      localStorage.removeItem("mango_premium_sub");
      localStorage.removeItem("mango_active_product_ids");
    },
  },
});

export const {
  fetchPackagesBegin,
  fetchPackagesSuccess,
  fetchPackagesError,
  fetchProductsBegin,
  fetchProductsSuccess,
  fetchProductsError,
  setActiveSubscription,
  setPremiumSub,
  setActiveProductIds,
  setLoading,
  openSubscriptionModal,
  closeSubscriptionModal,
  setPaymentInitiated,
  fetchHistoryBegin,
  fetchHistorySuccess,
  resetSubscription,
} = subscriptionSlice.actions;

export default subscriptionSlice.reducer;
